/**
 * ClinicStaffing — scorecard sorted by lowest staffing % first.
 * Requisitions removed. Open positions derived. Waitlist column added.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtMonYY } from '../../lib/date';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ExternalLink, Pin } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A', ok: '#3B6F5D',
};

const STATUS_STYLE = {
  green:  { bg: '#EDF3EF', bar: '#3B6F5D', label: 'FULLY STAFFED' },
  yellow: { bg: '#FBF3E0', bar: '#B08A3A', label: 'UNDERSTAFFED' },
  red:    { bg: '#FBECEC', bar: '#B54B4B', label: 'CRITICAL' },
};

function ManualPinRow({ clinic, initial, onSaved }) {
  const [pin, setPin] = React.useState(initial || 0);
  const [saving, setSaving] = React.useState(false);
  const [onHold, setOnHold] = React.useState(false);
  const [holdReason, setHoldReason] = React.useState('Staffing gap — no new evaluations');
  React.useEffect(() => {
    setPin(initial || 0);
    api.get('/hr/settings').then(({ data }) => {
      const h = (data.clinic_hold_statuses || {})[clinic];
      setOnHold(!!(h && h.on_hold));
      if (h?.reason) setHoldReason(h.reason);
    }).catch(() => {});
  }, [initial, clinic]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.get('/hr/settings');
      const pins  = { ...(data.manual_position_pins || {}) };
      const holds = { ...(data.clinic_hold_statuses || {}) };
      const n = Math.max(0, parseInt(pin || 0, 10) || 0);
      if (n === 0) delete pins[clinic]; else pins[clinic] = n;
      if (onHold) holds[clinic] = { on_hold: true, reason: holdReason,
                                     since: (holds[clinic]?.since) || new Date().toISOString().slice(0,10) };
      else        delete holds[clinic];
      await api.patch('/hr/settings', { manual_position_pins: pins, clinic_hold_statuses: holds });
      toast.success('Clinic settings saved');
      onSaved && onSaved();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t space-y-2"
         style={{ borderColor: C.border }} data-testid="hr-manual-pin">
      <div className="flex items-center gap-2 flex-wrap">
        <Pin size={13} style={{ color: C.plum }} />
        <div className="text-[11px]" style={{ color: C.ink2 }}>Pin extra positions (additive to demand-driven):</div>
        <Input type="number" min="0" value={pin} onChange={e => setPin(e.target.value)}
               className="h-7 w-20" data-testid="hr-manual-pin-input" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-2 text-[11px] font-semibold" style={{ color: onHold ? '#B54B4B' : C.ink2 }}>
          <input type="checkbox" checked={onHold} onChange={e => setOnHold(e.target.checked)} data-testid="hr-clinic-hold-toggle" />
          Mark clinic ON HOLD for new evaluations
        </label>
        {onHold && (
          <Input value={holdReason} onChange={e => setHoldReason(e.target.value)}
                 className="h-7 flex-1 min-w-40" placeholder="Reason (shown on attention list)"
                 data-testid="hr-clinic-hold-reason" />
        )}
      </div>
      <Button size="sm" onClick={save} disabled={saving} data-testid="hr-manual-pin-save">
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status?.color] || STATUS_STYLE.green;
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="inline-flex text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
            style={{ background: s.bg, color: s.bar }} data-testid={`hr-status-${status?.color}`}>{s.label}</span>
      {status?.od_vacant && (
        <span className="inline-flex text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
              style={{ background: '#FBECEC', color: '#B54B4B' }} data-testid="hr-status-od-vacant">OD Vacant</span>
      )}
    </div>
  );
}

function MixChip({ label, n, tone }) {
  if (!n) return null;
  return (
    <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded mr-1"
          style={{ background: `${tone}15`, color: tone }}>{label}·{n}</span>
  );
}

export default function ClinicStaffing() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get('/hr/staffing')
      .then(({ data }) => { setRows(data.rows || []); setSummary(data.summary || null); })
      .catch(() => toast.error('Could not load clinic staffing'))
      .finally(() => setLoading(false));
  }, []);

  const openClinic = async (clinic) => {
    setSelected(clinic); setDetail(null); setDetailLoading(true);
    try {
      const { data } = await api.get(`/hr/staffing/${encodeURIComponent(clinic)}`);
      setDetail(data);
    } catch { toast.error('Could not load clinic detail'); }
    finally { setDetailLoading(false); }
  };

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: C.soft }}>Loading Clinic Staffing…</div>;

  return (
    <div className="space-y-4" data-testid="hr-clinic-staffing">
      {summary && (
        <div className="flex flex-wrap gap-2 text-[11px]" data-testid="hr-staffing-summary">
          <span className="px-2 py-1 rounded" style={{ background: '#EDF3EF', color: C.ok }}>{summary.green} fully staffed</span>
          <span className="px-2 py-1 rounded" style={{ background: '#FBF3E0', color: C.gold }}>{summary.yellow} understaffed</span>
          <span className="px-2 py-1 rounded" style={{ background: '#FBECEC', color: '#B54B4B' }}>{summary.red} critical</span>
          {summary.od_vacant > 0 && <span className="px-2 py-1 rounded font-semibold" style={{ background: '#FBECEC', color: '#B54B4B' }}>{summary.od_vacant} OD vacant</span>}
          <span className="px-2 py-1 rounded" style={{ background: '#F4F1F8', color: C.plum }}>{summary.total_open_positions} to hire · {summary.total_positions_capacity || 0} capacity</span>
          {summary.total_waitlist > 0 && <span className="px-2 py-1 rounded font-semibold" style={{ background: '#FFF3EF', color: C.rose }}>{summary.total_waitlist} waiting</span>}
          <span className="ml-auto italic" style={{ color: C.soft }}>Sorted lowest staffing % first</span>
        </div>
      )}

      <Card className="border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: C.paper }}>
              <tr className="text-[10px] uppercase tracking-wider" style={{ color: C.ink2 }}>
                <th className="text-left px-4 py-3">Clinic</th>
                <th className="text-left px-4 py-3">Office Director</th>
                <th className="text-left px-4 py-3">Core Filled</th>
                <th className="text-left px-4 py-3">Staffing</th>
                <th className="text-left px-4 py-3">Credential Mix</th>
                <th className="text-center px-3 py-3">Supplemental</th>
                <th className="text-center px-3 py-3">To Hire</th>
                <th className="text-center px-3 py-3">Incoming</th>
                <th className="text-center px-3 py-3">Departing</th>
                <th className="text-center px-3 py-3">Waitlist</th>
                <th className="text-center px-3 py-3">90d Attr.</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.clinic}
                    className="border-t cursor-pointer hover:bg-[#FBF8F4] transition-colors"
                    style={{ borderColor: C.border }}
                    onClick={() => openClinic(r.clinic)}
                    data-testid={`hr-scorecard-row-${r.clinic}`}>
                  <td className="px-4 py-3 font-semibold" style={{ color: C.ink }}>{r.clinic}</td>
                  <td className="px-4 py-3" style={{ color: C.ink2 }}>{r.office_director || <span style={{ color: '#B54B4B' }}>—</span>}</td>
                  <td className="px-4 py-3" style={{ color: C.ink2 }}>{r.core_filled} / {r.core_target}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-serif text-lg" style={{ color: C.ink }}>{r.core_pct}%</span>
                      <StatusBadge status={r.status} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <MixChip label="SLP"    n={r.credential_mix?.SLP}  tone={C.plum} />
                    <MixChip label="CF"     n={r.credential_mix?.CF}   tone={C.teal} />
                    <MixChip label="SLPA"   n={r.credential_mix?.SLPA} tone={C.gold} />
                    <MixChip label="PRN"    n={r.credential_mix?.PRN}  tone={C.rose} />
                    <MixChip label="Extern" n={r.credential_mix?.Extern} tone={C.ink2} />
                  </td>
                  <td className="px-3 py-3 text-center" style={{ color: C.ink2 }}>{r.supplemental_count}</td>
                  <td className="px-3 py-3 text-center" style={{ color: r.open_positions > 0 ? C.rose : C.ink2, fontWeight: r.open_positions > 0 ? 600 : 400 }}>{r.open_positions}</td>
                  <td className="px-3 py-3 text-center" style={{ color: C.ink2 }}>{r.incoming_count}</td>
                  <td className="px-3 py-3 text-center" style={{ color: r.departing_count > 0 ? C.rose : C.ink2 }}>{r.departing_count}</td>
                  <td className="px-3 py-3 text-center" style={{ color: r.waitlist_count > 0 ? C.rose : C.ink2, fontWeight: r.waitlist_count > 0 ? 600 : 400 }}>{r.waitlist_count}</td>
                  <td className="px-3 py-3 text-center" style={{ color: C.ink2 }}>{r.attrition_90d}</td>
                  <td className="px-3 py-3 text-right"><ExternalLink size={14} style={{ color: C.soft }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={!!selected} onOpenChange={o => { if (!o) { setSelected(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="hr-clinic-detail-drawer">
          <SheetHeader><SheetTitle>{selected} · Clinic detail</SheetTitle></SheetHeader>
          {detailLoading && <div className="p-6 text-sm text-center" style={{ color: C.soft }}>Loading…</div>}
          {detail && (
            <div className="space-y-5 mt-4">
              <Card className="p-4 border" style={{ borderColor: C.border }}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-serif text-3xl" style={{ color: C.ink }}>{detail.scorecard?.core_pct}%</div>
                  <StatusBadge status={detail.scorecard?.status} />
                  <div className="ml-auto text-[11px] text-right" style={{ color: C.soft }}>
                    {detail.scorecard?.core_filled} / {detail.scorecard?.core_target} core seats · {detail.scorecard?.active_headcount} active total
                    {detail.scorecard?.open_positions > 0 && (
                      <div className="font-semibold" style={{ color: C.rose }}>{detail.scorecard.open_positions} position{detail.scorecard.open_positions === 1 ? '' : 's'} to hire</div>
                    )}
                  </div>
                </div>
                <ManualPinRow clinic={selected} initial={detail.scorecard?.manual_pin || 0} onSaved={() => openClinic(selected)} />
              </Card>

              <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-clinic-headcount-chart">
                <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>Headcount · 12 months</div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={detail.headcount_series || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                      <XAxis dataKey="month" fontSize={10} tickLine={false} tickFormatter={fmtMonYY} />
                      <YAxis fontSize={10} tickLine={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="headcount" stroke={C.plum} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-clinic-active-staff">
                <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
                     style={{ borderColor: C.border, background: C.paper, color: C.ink2 }}>
                  Active staff · {detail.active_staff?.length || 0}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>
                      <th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Role</th>
                      <th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Start</th>
                      <th className="text-right px-4 py-2">Tenure</th>
                    </tr></thead>
                    <tbody>
                      {(detail.active_staff || []).map(s => (
                        <tr key={s.clinician_id} className="border-t" style={{ borderColor: C.border }}>
                          <td className="px-4 py-2" style={{ color: C.ink }}>
                            {s.name}
                            {s.is_od && <span className="ml-1 text-[9px] font-bold" style={{ color: C.gold }}>· OD</span>}
                            {s.on_leave && <span className="ml-1 text-[9px] font-bold" style={{ color: C.rose }}>· ON LEAVE</span>}
                            {s.termination_date && <span className="ml-1 text-[9px] font-bold" style={{ color: C.rose }}>· LEAVING {s.termination_date}</span>}
                          </td>
                          <td className="px-4 py-2" style={{ color: C.ink2 }}>{s.role}</td>
                          <td className="px-4 py-2" style={{ color: C.ink2 }}>{s.employment_type}</td>
                          <td className="px-4 py-2" style={{ color: C.ink2 }}>{s.start_date || '—'}</td>
                          <td className="px-4 py-2 text-right" style={{ color: C.ink2 }}>
                            {s.tenure_days == null ? '—' : `${Math.round(s.tenure_days / 30.4)} mo`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {(detail.waitlisted_clients?.length > 0 || detail.hold_scheduling_clients?.length > 0) && (
                <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-clinic-waitlist">
                  <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
                       style={{ borderColor: C.border, background: '#FFF3EF', color: C.rose }}>
                    Clients waiting on staffing · {(detail.waitlisted_clients?.length || 0) + (detail.hold_scheduling_clients?.length || 0)}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {(detail.waitlisted_clients || []).map(c => (
                        <tr key={c.client_id} className="border-t" style={{ borderColor: C.border }}>
                          <td className="px-4 py-2" style={{ color: C.ink }}>{c.client_name}</td>
                          <td className="px-4 py-2 text-[10px]" style={{ color: C.soft }}>Waitlisted · {c.service_type || '—'}</td>
                          <td className="px-4 py-2 text-right text-[10px]" style={{ color: C.soft }}>since {c.waitlist_date || '—'}</td>
                        </tr>
                      ))}
                      {(detail.hold_scheduling_clients || []).map(c => (
                        <tr key={c.client_id} className="border-t" style={{ borderColor: C.border }}>
                          <td className="px-4 py-2" style={{ color: C.ink }}>{c.client_name}</td>
                          <td className="px-4 py-2 text-[10px]" style={{ color: C.soft }}>On hold · {c.hold_reason}</td>
                          <td className="px-4 py-2 text-right text-[10px]" style={{ color: C.soft }}>since {c.hold_date || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}

              <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-clinic-separations">
                <div className="px-4 py-3 border-b text-[10px] uppercase tracking-widest font-bold"
                     style={{ borderColor: C.border, background: C.paper, color: C.ink2 }}>
                  Separations · last 12 months · {detail.recent_separations?.length || 0}
                </div>
                {(detail.recent_separations?.length || 0) === 0 ? (
                  <div className="p-4 text-sm text-center" style={{ color: C.soft }}>No separations recorded</div>
                ) : (
                  <table className="w-full text-sm"><tbody>
                    {detail.recent_separations.map(s => (
                      <tr key={s.separation_id} className="border-t" style={{ borderColor: C.border }}>
                        <td className="px-4 py-2" style={{ color: C.ink }}>{s.clinician_name}</td>
                        <td className="px-4 py-2" style={{ color: C.ink2 }}>{s.role}</td>
                        <td className="px-4 py-2" style={{ color: C.ink2 }}>{s.primary_reason}</td>
                        <td className="px-4 py-2 text-right" style={{ color: C.soft }}>{s.last_day}</td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
